import React from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, Avatar,
  Button, Tooltip, Menu, MenuItem, ListItemIcon, Divider, useMediaQuery, useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import LogoutIcon from '@mui/icons-material/Logout';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SideMenu, { menuItems } from './SideMenu';
import PaletteDialog from './PaletteDialog';

const drawerWidth = 264;
const roleLabels = { curator: 'Куратор', operator: 'Оператор', courier: 'Курьер' };

const MainLayout = () => {
  const [open, setOpen] = React.useState(false);
  const [accountAnchor, setAccountAnchor] = React.useState(null);
  const [appearanceOpen, setAppearanceOpen] = React.useState(false);
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const entries = menuItems.flatMap(item => item.submenu || [item]);
  const currentPage = entries.find(item => item.path === `${location.pathname}${location.search}`)
    || entries.find(item => item.path?.split('?')[0] === location.pathname);
  const title = currentPage?.text || 'Панель управления';
  const userName = currentUser?.name || currentUser?.username || 'Пользователь';
  const role = roleLabels[currentUser?.role] || 'Личный кабинет';

  const handleDrawerToggle = () => setOpen(previous => !previous);
  const closeAccount = () => setAccountAnchor(null);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <a className="skip-link" href="#main-content">Перейти к содержимому</a>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.default', backgroundImage: 'none', color: 'text.primary',
          borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0,
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important', gap: 2, px: { xs: 2, md: 4 } }}>
          <IconButton aria-label="Открыть навигацию" edge="start" onClick={handleDrawerToggle} sx={{ display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Рабочее пространство</Typography>
            <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 16, display: { xs: 'none', sm: 'block' } }} />
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{title}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', lg: 'block' }, textTransform: 'capitalize' }}>
            {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())}
          </Typography>
          <Tooltip title="Личный кабинет">
            <IconButton aria-label="Открыть меню профиля" aria-haspopup="menu" aria-expanded={Boolean(accountAnchor)} onClick={event => setAccountAnchor(event.currentTarget)} sx={{ p: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.selected', color: 'primary.main', fontSize: 13, fontWeight: 700 }}>{userName.charAt(0).toUpperCase()}</Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={desktop ? 'permanent' : 'temporary'}
        anchor="left"
        open={desktop || open}
        onClose={() => setOpen(false)}
        sx={{
          width: { md: drawerWidth }, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth, boxSizing: 'border-box', bgcolor: 'background.sidebar',
            backgroundImage: 'none', borderRight: '1px solid', borderColor: 'divider', borderRadius: 0,
          },
        }}
      >
        <Box sx={{ height: 72, flexShrink: 0, display: 'flex', alignItems: 'center', px: 3, gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ width: 33, height: 33, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main', border: `1px solid ${alpha(theme.palette.primary.main, 0.33)}`, boxShadow: `0 0 16px ${alpha(theme.palette.primary.main, 0.06)}`, borderRadius: '9px' }}>
            <GridViewRoundedIcon sx={{ fontSize: 21 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 19, letterSpacing: '-0.06em' }}>panel<Box component="span" sx={{ color: 'primary.main' }}>.</Box></Typography>
          <Typography variant="overline" color="text.secondary" sx={{ ml: 'auto', fontSize: 9 }}>DEV / 2.0</Typography>
          {!desktop && <IconButton size="small" aria-label="Закрыть навигацию" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>}
        </Box>
        <SideMenu onNavigate={() => setOpen(false)} />
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth color="inherit" onClick={event => setAccountAnchor(event.currentTarget)}
            aria-label="Меню профиля" aria-haspopup="menu" aria-expanded={Boolean(accountAnchor)}
            sx={{ justifyContent: 'flex-start', gap: 1.25, px: 1, textAlign: 'left' }}
          >
            <Avatar sx={{ width: 35, height: 35, bgcolor: alpha(theme.palette.secondary.main, 0.12), color: 'secondary.light', fontSize: 14 }}>{userName.charAt(0).toUpperCase()}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 650 }}>{userName}</Typography>
              <Typography variant="caption" color="text.secondary">{role}</Typography>
            </Box>
            <UnfoldMoreIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
          </Button>
        </Box>
      </Drawer>

      <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={closeAccount}>
        <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
          <Typography variant="subtitle2" noWrap>{userName}</Typography>
          <Typography variant="caption" color="text.secondary">{role}</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { closeAccount(); setOpen(false); setAppearanceOpen(true); }}>
          <ListItemIcon><PaletteOutlinedIcon fontSize="small" /></ListItemIcon>Оформление
        </MenuItem>
        <MenuItem onClick={() => { closeAccount(); setOpen(false); navigate('/change-password'); }}>
          <ListItemIcon><LockOutlinedIcon fontSize="small" /></ListItemIcon>Сменить пароль
        </MenuItem>
        <MenuItem onClick={() => { closeAccount(); logout(); navigate('/login'); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Выйти
        </MenuItem>
      </Menu>
      {appearanceOpen && <PaletteDialog open onClose={() => setAppearanceOpen(false)} />}

      <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }, p: { xs: 2, sm: 3, lg: 4 }, outline: 'none' }}>
        <Toolbar sx={{ minHeight: '72px !important' }} /> {/* This creates space below the AppBar */}
        <Box className="page-content" sx={{ maxWidth: 1800, mx: 'auto' }}><Outlet /></Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
